import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { catchError, throwError, switchMap, filter, take } from 'rxjs';

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const token = authService.getToken();

  let authReq = req.clone({
    withCredentials: true,
  });

  if (token) {
    authReq = authReq.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  const isStateMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(
    req.method
  );
  if (isStateMutation) {
    const csrfToken = getCookie('XSRF-TOKEN');
    if (csrfToken) {
      authReq = authReq.clone({
        setHeaders: {
          'X-CSRF-Token': csrfToken,
        },
      });
    }
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      const currentUrl = router.url;

      if (error.status === 403 && error.error?.passwordChanged) {
        alert(
          'Your session has expired due to security measures. Please log in again.'
        );

        authService.clearAuthData();

        if (!currentUrl.includes('/login')) {
          router.navigate(['/login'], {
            queryParams: {
              message: 'Please log in again with your new password',
            },
          });
        }

        return throwError(
          () => new Error('Session expired due to password change')
        );
      }

      const isTokenExpiredError =
        error.status === 401 ||
        (error.status === 403 &&
          (error.error?.message?.includes('expired') ||
            error.error?.message?.includes('Token has expired') ||
            error.error?.message === 'Invalid or expired token'));

      if (isTokenExpiredError) {
        const isAuthEndpoint =
          req.url.includes('/auth/login') ||
          req.url.includes('/auth/register') ||
          req.url.includes('/auth/refresh-token');

        if (isAuthEndpoint) {
          authService.clearAuthData();
          if (
            !currentUrl.includes('/login') &&
            !currentUrl.includes('/register') &&
            !currentUrl.includes('/forgot-password') &&
            !currentUrl.includes('/reset-password')
          ) {
            router.navigate(['/login'], {
              queryParams: { returnUrl: currentUrl },
            });
          }

          return throwError(() => new Error('Authentication required'));
        }

        if (authService.isRefreshingToken) {
          return authService.refreshTokenObservable.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((newToken) => {
              let retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newToken}`,
                },
                withCredentials: true,
              });

              if (isStateMutation) {
                const csrfToken = getCookie('XSRF-TOKEN');
                if (csrfToken) {
                  retryReq = retryReq.clone({
                    setHeaders: {
                      'X-CSRF-Token': csrfToken,
                    },
                  });
                }
              }

              return next(retryReq);
            }),
            catchError(() => {
              return throwError(() => new Error('Session expired'));
            })
          );
        }

        return authService.refreshToken().pipe(
          switchMap((refreshResponse) => {
            const newToken = refreshResponse.token;
            let retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
              },
              withCredentials: true,
            });

            if (isStateMutation) {
              const csrfToken = getCookie('XSRF-TOKEN');
              if (csrfToken) {
                retryReq = retryReq.clone({
                  setHeaders: {
                    'X-CSRF-Token': csrfToken,
                  },
                });
              }
            }

            return next(retryReq);
          }),
          catchError(() => {
            authService.clearAuthData();

            if (
              !currentUrl.includes('/login') &&
              !currentUrl.includes('/register') &&
              !currentUrl.includes('/forgot-password') &&
              !currentUrl.includes('/reset-password')
            ) {
              router.navigate(['/login'], {
                queryParams: {
                  returnUrl: currentUrl,
                  message: 'Your session has expired. Please log in again.',
                },
              });
            }

            return throwError(() => new Error('Session expired'));
          })
        );
      }

      if (error.status === 403) {
        return throwError(() => new Error('Access denied'));
      }

      return throwError(() => error);
    })
  );
};
